import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import './styles.css'
import { pad, parseTime, toHM } from './utils'

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1)
const MINUTES = Array.from({ length: 60 }, (_, i) => i)
const POP_W = 240
const POP_H = 250

/**
 * Time input — one field opening a popup with three columns: HH (1–12),
 * MM (00–59) and AM/PM.
 *
 * Drop-in for `<input type="time">`: `value` is a 24h 'HH:mm' string and
 * `onChange` receives `{ target: { name, id, value } }`.
 *
 * The popup is portalled to <body> with fixed positioning, so no `overflow:
 * hidden` ancestor can clip it and it never displaces the surrounding form.
 */
export default function TimeField({
  id,
  name,
  value,
  onChange,
  required = false,
  disabled = false,
  placeholder = 'hh : mm am/pm',
  className = 'field',
}) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState(null)
  const wrapRef = useRef(null)
  const popRef = useRef(null)
  const centred = useRef(false)

  /* Uncontrolled fallback, so this drops into places where the native input
     carried no value/onChange either. */
  const [inner, setInner] = useState('')
  const controlled = value !== undefined
  const { hour, minute, meridiem } = parseTime(controlled ? value : inner)

  /** Anchor under the input, flipping above when the viewport runs out. */
  const place = useCallback(() => {
    const r = wrapRef.current?.getBoundingClientRect()
    if (!r) return
    const below = window.innerHeight - r.bottom
    const top = below < POP_H + 12 && r.top > POP_H + 12 ? r.top - POP_H - 6 : r.bottom + 6
    const left = Math.min(Math.max(8, r.left), window.innerWidth - POP_W - 8)
    // Keep the same object when nothing moved, so this can't churn renders.
    setPos((p) => (p && p.top === top && p.left === left ? p : { top, left }))
  }, [])

  useLayoutEffect(() => {
    if (open) place()
  }, [open, place])

  useEffect(() => {
    if (!open) return
    const onDown = (e) => {
      if (!wrapRef.current?.contains(e.target) && !popRef.current?.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    // Capture-phase, so scrolling any ancestor keeps the popup anchored — but
    // scrolling a column inside it is the user browsing, not the page moving.
    const onScroll = (e) => {
      if (!popRef.current?.contains(e.target)) place()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', place)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', place)
    }
  }, [open, place])

  /* Opening with no value pre-selects the current time, so the columns start
   * at "now" instead of an empty 12:00 AM — one less thing to dial in. */
  useEffect(() => {
    if (open && hour == null) {
      const now = new Date()
      emit({ hour: now.getHours() % 12 || 12, minute: now.getMinutes(), meridiem: now.getHours() >= 12 ? 'PM' : 'AM' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  /* Centre the selected hour/minute in their own columns, exactly once per
   * open. Re-running on a reposition would yank the list back while the user
   * is scrolling it. scrollTop is set directly because scrollIntoView would
   * scroll the page along with the column. The hour/minute deps matter on a
   * first open: the current-time default lands one render after the popup, so
   * the first pass finds no `.sel` cells and must not count as centred. */
  useEffect(() => {
    if (!open) {
      centred.current = false
      return
    }
    if (centred.current || !popRef.current) return
    let found = false
    popRef.current.querySelectorAll('.tf-col-list').forEach((list) => {
      const sel = list.querySelector('.tf-cell.sel')
      if (sel) {
        found = true
        list.scrollTop = sel.offsetTop - list.offsetTop - list.clientHeight / 2 + sel.clientHeight / 2
      }
    })
    if (found) centred.current = true
  }, [open, pos, hour, minute, meridiem])

  /* Any single pick emits a complete time — unset parts default to 12:00 AM. */
  const emit = (next) => {
    const h = next.hour ?? hour ?? 12
    const min = next.minute ?? minute ?? 0
    const mer = next.meridiem ?? meridiem ?? 'AM'
    const val = toHM(h, min, mer)
    if (!controlled) setInner(val)
    onChange?.({ target: { name, id, value: val } })
  }

  const column = (label, items, selected, render, onPick) => (
    <div>
      <p className="tf-col-label">{label}</p>
      <div className="tf-col-list">
        {items.map((it) => (
          <button
            key={it}
            type="button"
            className={`tf-cell${it === selected ? ' sel' : ''}`}
            onClick={() => onPick(it)}
          >
            {render(it)}
          </button>
        ))}
      </div>
    </div>
  )

  return (
    <div className="tf-wrap" ref={wrapRef}>
      {/* Deliberately not `readOnly`: readonly controls are barred from HTML
          constraint validation, so `required` would be silently inert in forms
          that rely on it (kundli-studio's does). Typing and pasting are blocked
          instead, and inputMode="none" keeps the mobile keyboard shut. */}
      <input
        id={id}
        name={name}
        className={className}
        value={hour == null ? '' : `${pad(hour)} : ${pad(minute)} ${meridiem}`}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        inputMode="none"
        autoComplete="off"
        onChange={() => {}}
        onKeyDown={(e) => { if (e.key !== 'Tab') e.preventDefault() }}
        onPaste={(e) => e.preventDefault()}
        onClick={() => !disabled && setOpen((o) => !o)}
      />
      {open && pos && createPortal(
        <div className="tf-pop" ref={popRef} style={{ top: pos.top, left: pos.left, width: POP_W }}>
          <div className="tf-cols">
            {column('HH', HOURS, hour, pad, (h) => emit({ hour: h }))}
            {column('MM', MINUTES, minute, pad, (min) => emit({ minute: min }))}
            {column('AM/PM', ['AM', 'PM'], meridiem, (x) => x, (mer) => emit({ meridiem: mer }))}
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
